import { supabase } from "@/integrations/supabase/client";

// VAPID public key - this should be configured in environment variables
// For testing, using generated keys. Replace with your environment variables in production.
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BBA-OABW7er5d7MrKIk-RySAHqRJMXsvUz98FJeUF_OG1WoZPNw7lLW3ZZzpl-8TN7tLkPsEnV3V2rGPQSuJDfs';

console.log('VAPID public key configured:', !!VAPID_PUBLIC_KEY);

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  expirationTime?: number;
}

/**
 * Convert VAPID public key from base64 to Uint8Array
 */
function base64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get current push subscription
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('Error getting push subscription:', error);
    return null;
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(): Promise<PushSubscriptionData | null> {
  console.log('Checking push support...');

  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported');
  }

  console.log('Push supported, checking VAPID key...');

  if (!VAPID_PUBLIC_KEY) {
    console.error('VAPID public key not configured:', VAPID_PUBLIC_KEY);
    throw new Error('VAPID public key not configured. Please set VITE_VAPID_PUBLIC_KEY in your environment variables.');
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Request notification permission first
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
    } else if (Notification.permission !== 'granted') {
      throw new Error('Notification permission required');
    }

    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    console.log('Push subscription created:', subscription);

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
        auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
      },
      expirationTime: subscription.expirationTime || undefined,
    };
  } catch (error) {
    console.error('Error subscribing to push:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<void> {
  try {
    const subscription = await getCurrentSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log('Push subscription removed');
    }
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
  }
}

/**
 * Save push subscription to backend
 */
export async function savePushSubscription(subscriptionData: PushSubscriptionData): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ subscription: subscriptionData }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save subscription');
    }

    console.log('Push subscription saved to backend');
  } catch (error) {
    console.error('Error saving push subscription:', error);
    throw error;
  }
}

/**
 * Remove push subscription from backend
 */
export async function removePushSubscription(): Promise<void> {
  try {
    // Get current subscription and remove it from backend by endpoint
    const subscription = await getCurrentSubscription();
    if (subscription) {
      // For now, we'll just log - in a full implementation, you'd call an API to remove by endpoint
      console.log('Push subscription would be removed from backend:', subscription.endpoint);
    }
  } catch (error) {
    console.error('Error removing push subscription:', error);
  }
}
